import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import CorporateFareIcon from '@mui/icons-material/CorporateFare'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import { Grid } from '@material-ui/core'
import { Paper, SpacingContainer } from '../../../components/common/Containers'
import SpacingContainerBase from '../../../components/layouts/SpacingContainerBase'
import Typography from '../../../components/common/Typography'
import { Divider } from '../../../components/common/Divider'
import NothingFoundCard from '../../../components/generics/Cards/NothingFoundCard'
import { Table, TableHead, TableSortCell } from '../../../components/common/ResponsiveTable'
import { emptyArray } from '../../../utils/arrayUtils'
import { useTrans } from '../../../services/i18n'
import Item from './Item'

const SORT_ACCESSORS = {
  organization: item => item.orgName || '',
  status: item => (item.orgActive ? 'Active' : 'Deactivated'),
  role: item => (item.owner ? 'Owner' : item.role || ''),
  owner: item => item.ownerName || '',
}

const SORT_DIRECTION_ICONS = {
  asc: KeyboardArrowUpIcon,
  desc: KeyboardArrowDownIcon,
}

const sortIcon = order => () => {
  const Icon = SORT_DIRECTION_ICONS[order] || UnfoldMoreIcon
  return <Icon fontSize="small" />
}

const AssignedOrganizations = ({ organizations }) => {
  const { trans } = useTrans()
  const [[orderBy, orderDir], setSort] = useState(['organization', 'asc'])

  const rows = useMemo(() => {
    const accessor = SORT_ACCESSORS[orderBy] || SORT_ACCESSORS.organization
    const sorted = [...organizations].sort((a, b) =>
      String(accessor(a)).localeCompare(String(accessor(b)), undefined, { sensitivity: 'base' })
    )
    return orderDir === 'desc' ? sorted.reverse() : sorted
  }, [organizations, orderBy, orderDir])

  const sortCell = label => ({
    orderLabel: label,
    orderBy,
    orderDir,
    onChange: setSort,
    renderIcon: sortIcon,
    align: 'left',
  })

  return (
    <SpacingContainerBase py={0}>
      <Paper component={SpacingContainer} py={3} px="5sm3">
        <Grid container justifyContent="space-between" alignItems="center" spacing={3}>
          <Grid item xs zeroMinWidth>
            <Typography variant="h4" weight="bold">
              <CorporateFareIcon /> {trans('assigned-organizations')}
            </Typography>
          </Grid>
        </Grid>

        <SpacingContainer px="0" py={2}>
          <Divider light />
          {emptyArray(organizations) ? (
            <NothingFoundCard px={5} py={10} title={trans('no-organizations-assigned-yet')} size="medium" />
          ) : (
            <Table
              head={
                <TableHead>
                  <TableSortCell {...sortCell('organization')}>{trans('organization')}</TableSortCell>
                  <TableSortCell {...sortCell('status')}>{trans('organization-status')}</TableSortCell>
                  <TableSortCell {...sortCell('role')}>{trans('role')}</TableSortCell>
                  <TableSortCell {...sortCell('owner')}>{trans('owner')}</TableSortCell>
                </TableHead>
              }
            >
              {rows.map(item => (
                <Item key={item.id} item={item} />
              ))}
            </Table>
          )}
        </SpacingContainer>
      </Paper>
    </SpacingContainerBase>
  )
}

AssignedOrganizations.propTypes = {
  organizations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      organizationId: PropTypes.string,
      orgName: PropTypes.string,
      orgActive: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
      owner: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
      role: PropTypes.string,
      ownerName: PropTypes.string,
    })
  ),
}

AssignedOrganizations.defaultProps = {
  organizations: [],
}

export default AssignedOrganizations
